import type { ErrorResponse } from '@translate-ai/shared';
import { useAuthStore } from '@/store/auth';
import { parseSseFrames, type SseEvent } from '@/lib/sse';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '/api';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
  /** If true, do not try to refresh on 401. */
  skipRefresh?: boolean;
  /** If true, return the raw Response (for streamed downloads). */
  raw?: boolean;
}

async function doRefresh(): Promise<string | null> {
  try {
    const resp = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!resp.ok) return null;
    const data = (await resp.json()) as { accessToken?: string };
    if (!data.accessToken) return null;
    useAuthStore.getState().setAccessToken(data.accessToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

// Single-flight: concurrent 401s (e.g. the startup prefetches racing with an
// expired token) share one POST /auth/refresh instead of firing one each.
let refreshInFlight: Promise<string | null> | null = null;

function tryRefresh(): Promise<string | null> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function apiFetch<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers = {}, skipRefresh, raw, ...rest } = options;

  const buildRequest = (token: string | null): RequestInit => {
    const finalHeaders: Record<string, string> = { ...headers };
    if (body !== undefined && !(body instanceof FormData)) {
      finalHeaders['Content-Type'] = 'application/json';
    }
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
    return {
      ...rest,
      headers: finalHeaders,
      credentials: 'include',
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
          ? body
          : JSON.stringify(body),
    };
  };

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  let token = useAuthStore.getState().accessToken;
  let resp = await fetch(url, buildRequest(token));

  if (resp.status === 401 && !skipRefresh) {
    const newToken = await tryRefresh();
    if (newToken) {
      token = newToken;
      resp = await fetch(url, buildRequest(token));
    } else {
      useAuthStore.getState().clear();
    }
  }

  if (!resp.ok) {
    let payload: ErrorResponse | null = null;
    try {
      payload = (await resp.json()) as ErrorResponse;
    } catch {
      // non-JSON error body
    }
    const code = payload?.error?.code ?? `HTTP_${resp.status}`;
    const message = payload?.error?.message ?? `Request failed with status ${resp.status}`;
    throw new ApiError(resp.status, code, message, payload?.error?.details);
  }

  if (raw) return resp as unknown as T;
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/**
 * POSTs `body` and consumes a Server-Sent Events response, invoking `onEvent`
 * for each parsed frame. Reuses {@link apiFetch}'s auth + 401-refresh handling,
 * so a pre-flight error (e.g. quota exceeded) arrives as a thrown {@link ApiError}
 * before the stream is read. Pass `signal` to cancel the in-flight stream.
 */
export async function apiSse(
  path: string,
  body: unknown,
  onEvent: (event: SseEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await apiFetch<Response>(path, { method: 'POST', body, raw: true, signal });
  const contentType = resp.headers.get('content-type') ?? '';
  if (!contentType.includes('text/event-stream') || !resp.body) {
    throw new ApiError(resp.status, 'BAD_STREAM', 'Expected a streaming response');
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSseFrames(buffer);
      buffer = rest;
      for (const event of events) onEvent(event);
    }
    // The server terminates every event with a blank line, so the loop drains
    // them all; this guards a final frame that lost its trailing separator.
    buffer += decoder.decode();
    if (buffer.trim()) {
      const { events } = parseSseFrames(`${buffer}\n\n`);
      for (const event of events) onEvent(event);
    }
  } finally {
    reader.releaseLock();
  }
}
